/**
 * useCompatResult — TanStack Query hook backing the /compat/:id result page.
 *
 * Endpoint: GET /api/v1/sku/compat-check/result/:id
 * Readable by BOTH parties (inviter + partner). Response is one of:
 *   { status: 'pending' } | { status: 'failed' } | { status: 'completed', check }
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export interface CompatResultCheck {
  scoreLove: number;
  scoreFriendship: number;
  scoreCommunication: number;
  scoreConflict: number;
  scoreOverall: number;
  reportMarkdown: string;
}

export interface CompatResultData {
  status: 'pending' | 'failed' | 'completed';
  check?: CompatResultCheck;
}

export function useCompatResult(id: string): {
  data: CompatResultData | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<CompatResultData, Error>({
    queryKey: ['compat-result-by-id', id],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch(`/api/v1/sku/compat-check/result/${encodeURIComponent(id)}`, jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`compat_result_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as CompatResultData;
    },
    enabled: !!jwt && !!id,
    staleTime: 30_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
